import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A panel's selection state: the selected path, the expanded and hidden sets,
 * and the path to Object3D map. Each shell owns its own. Paths are slash-joined
 * node names without a leading `./` (R3F-contracts.md).
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, useSyncExternalStore, } from 'react';
import { createExternalStore, useExternalStoreValue, } from '../hooks/createExternalStore.js';
const SelectionContext = createContext(null);
SelectionContext.displayName = 'SelectionContext';
/**
 * The selected path, in a store like the hover store. A node that asks only whether it is selected
 * re-renders when its answer flips, not on each selection, expand or hide. `SelectionProvider` owns
 * it, and its `selectedNodePath` reads from it.
 */
const SelectedNodePathStoreContext = createContext(null);
SelectedNodePathStoreContext.displayName = 'SelectedNodePathStoreContext';
export function SelectionProvider({ children }) {
    // Never-set state, so each factory runs once per provider.
    const [hoverStore] = useState(() => createExternalStore(null));
    const [selectedStore] = useState(() => createExternalStore(null));
    const selectedNodePath = useExternalStoreValue(selectedStore);
    const setSelectedNodePath = selectedStore.set;
    const [expandedNodePaths, setExpandedNodePathsState] = useState(() => new Set());
    const [hiddenNodePaths, setHiddenNodePathsState] = useState(() => new Set());
    const toggleExpandedNodePath = useCallback((path) => {
        setExpandedNodePathsState((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            }
            else {
                next.add(path);
            }
            return next;
        });
    }, []);
    const setExpandedNodePaths = useCallback((paths) => {
        setExpandedNodePathsState(new Set(paths));
    }, []);
    const toggleHidden = useCallback((path) => {
        setHiddenNodePathsState((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            }
            else {
                next.add(path);
            }
            return next;
        });
    }, []);
    const clearHidden = useCallback(() => {
        setHiddenNodePathsState(new Set());
    }, []);
    const nodeObjectMapRef = useRef(new Map());
    const objectPathMapRef = useRef(new WeakMap());
    const registerNodeObject = useCallback((path, object) => {
        // A same-path re-registration, such as a duplicate-named sibling, drops the
        // previous object's reverse entry, which names a path it no longer owns.
        const previous = nodeObjectMapRef.current.get(path);
        if (previous && previous !== object) {
            objectPathMapRef.current.delete(previous);
        }
        nodeObjectMapRef.current.set(path, object);
        objectPathMapRef.current.set(object, path);
    }, []);
    const unregisterNodeObject = useCallback((path) => {
        const object = nodeObjectMapRef.current.get(path);
        if (object)
            objectPathMapRef.current.delete(object);
        nodeObjectMapRef.current.delete(path);
    }, []);
    const clearAll = useCallback(() => {
        selectedStore.set(null);
        hoverStore.set(null);
        setExpandedNodePathsState(new Set());
        setHiddenNodePathsState(new Set());
        nodeObjectMapRef.current.clear();
        // Not objectPathMapRef: a WeakMap keyed by the Object3D itself, so the old
        // scene's entries become unreachable and are collected. A path key could
        // match the new scene, an object key cannot.
    }, [hoverStore, selectedStore]);
    const value = useMemo(() => ({
        selectedNodePath,
        hoverStore,
        expandedNodePaths,
        hiddenNodePaths,
        setSelectedNodePath,
        toggleExpandedNodePath,
        setExpandedNodePaths,
        toggleHidden,
        clearHidden,
        clearAll,
        nodeObjectMap: nodeObjectMapRef.current,
        objectPathMap: objectPathMapRef.current,
        registerNodeObject,
        unregisterNodeObject,
    }), [
        selectedNodePath,
        hoverStore,
        expandedNodePaths,
        hiddenNodePaths,
        setSelectedNodePath,
        toggleExpandedNodePath,
        setExpandedNodePaths,
        toggleHidden,
        clearHidden,
        clearAll,
        registerNodeObject,
        unregisterNodeObject,
    ]);
    return (_jsx(SelectionContext.Provider, { value: value, children: _jsx(SelectedNodePathStoreContext.Provider, { value: selectedStore, children: children }) }));
}
export function useSelection() {
    const value = useContext(SelectionContext);
    if (value === null) {
        throw new Error('useSelection must be used inside a <TscnPreviewShell> (SelectionProvider). ' +
            'See R3F-contracts.md §3.');
    }
    return value;
}
/**
 * Whether `path` is the selected node, false with no provider. The component re-renders when this
 * answer flips, so a selection change re-renders the old and the new node alone.
 */
export function useIsSelectedPath(path) {
    const store = useContext(SelectedNodePathStoreContext) ?? NO_PROVIDER_SELECTED_STORE;
    const isSelected = useCallback(() => path !== null && store.get() === path, [store, path]);
    return useSyncExternalStore(store.subscribe, isSelected, isSelected);
}
/**
 * `useSelection`, but `null` with no provider, for a canvas component that a
 * test mounts alone.
 */
export function useOptionalSelection() {
    return useContext(SelectionContext);
}
/**
 * The store with no `<SelectionProvider>`. It is safe to share, since a real
 * shell always has its own `hoverStore` and never writes here.
 */
const NO_PROVIDER_HOVER_STORE = createExternalStore(null);
const NO_PROVIDER_SELECTED_STORE = createExternalStore(null);
/**
 * The hovered node path. Only this component subscribes to hover changes, and
 * no selection, expand or hide change re-renders it.
 */
export function useHoveredNodePath() {
    const selection = useOptionalSelection();
    return useExternalStoreValue(selection?.hoverStore ?? NO_PROVIDER_HOVER_STORE);
}
//# sourceMappingURL=SelectionContext.js.map